import { CONFIG } from '../../Config/config'

import {
  processInventoryMovements as processInventoryMovementsGraphQLFunc,
  getInventory as getInventoryGraphQLFunc,
  getInventoryItem as getInventoryItemGraphQLFunc,
} from './../../shared/graphql/functions/inventory'
import { authorization } from '../Auth/Auth'
import store from '../../Config/store'
import * as ERRORS from '../../Reducers/Errors/const'
import {
  iteratePromisesInChunks,
  splitArrayIntoChunks,
} from '../../shared/libraries/utils/array'

/**
 * Call Graph QL Endpoint
 * @param {string} query
 * @param {object} variables
 * @returns {Promise<object>}
 */
export const callGraphQLEndpoint = function(
  authToken,
  query,
  variables,
  options = {},
) {
  var body = JSON.stringify({
    query,
    variables,
  })

  return new Promise((resolve, reject) => {
    fetch(CONFIG.GRAPHQL_API_BASE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': 'Bearer ' + authToken } : {}),
      },
      body,
    })
      .then(async response => {
        const result = await response.clone()
        const jsonResult = await result.json()
        if (jsonResult.errors && !options.ignoreErrors) {
          const error = jsonResult.errors[0]
          store.dispatch({
            type: ERRORS.PUSH_API_ERROR,
            error,
          })
          reject(error)
        } else {
          resolve(response)
        }
      })
      .catch(error => {
        reject(error)
      })
  })
}
export const callAuthorizedGraphQLEndpoint = (query, variables, options) => {
  const authToken = authorization.getAPIKey()
  return callGraphQLEndpoint(authToken, query, variables, options)
}

export const callTrailblazer = (query, variables) => {
  const authToken = authorization.getAPIKey()
  var body = JSON.stringify({
    query,
    variables,
  })
  return fetch(CONFIG.TRAILBLAZER_API_BASE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { 'Authorization': 'Bearer ' + authToken } : {}),
    },
    body,
  })
}

/**
 * Process an array of inventory movements
 * Example movement
 * {
 *   "quantity": 51,
 *   "inventoryId": 1,
 *   "cityId": 1,
 *   "comments": "Test",
 *   "ambassadorId": 1,
 *   "movement": 1,
 *   "dateTime": "2019-11-05T11:27:00+10:00"
 * }
 *
 * @param {Array<Movement>} movements
 * @returns {Promise<Array<null|Error>>}
 */
function processInventoryMovements(authToken, movements) {
  return callGraphQLEndpoint(authToken, processInventoryMovementsGraphQLFunc, {
    movements: movements,
  })
}

/**
 * Get inventory
 * @param {string} authToken
 * @param {object} params
 */
function getInventory(
  authToken,
  first,
  after,
  {
    cityId,
    searchText,
    clientId,
    typeId,
    ambassadorId,
    current,
    campaignId,
    active,
    includeLeftovers,
  },
) {
  return callGraphQLEndpoint(authToken, getInventoryGraphQLFunc, {
    first,
    after,
    cityId,
    clientId,
    typeId,
    searchText,
    current,
    ambassadorId,
    campaignId,
    active,
    includeLeftovers,
  })
}

/**
 * Get or search for inventory items.
 * @param {number} first Get the first x records, limit is 500
 * @param {string} after The cursort to start getting the records from
 * @param {number} cityId To filter inventory by this city
 * @param {number} clientId To filter inventory by this client
 * @param {number} typeId To filter inventory by this type
 * @param {string} searchText To search by inventory description
 * @param {number} ambassadorId To filter by ambassador
 * @param {Boolean} current Can only specify if ambassadorId is also specified.
 * If set to true then it will only return the inventory that the ambassador
 * currently has. If false it returns inventory the ambassador has had in the
 * past.
 * @returns {Array<InventoryItem>}
 */
const getPickupInventoryGraphQLFunc = `
  query getPickupInventoryData(
    $first: Int
    $merchCursor: String
    $flyerCodesCursor: String
    $cityId: ID!
    $clientIds: [ID]
    $searchText: String
    $searchFromStart: Boolean
    $ambassadorIds: [ID]
    $current: Boolean
    $campaignIds: [ID]
    $active: Boolean
    $includeLeftovers: Boolean
  ) {

    inventoryTypes {
      edges {
        node {
          id
          name
        }
      }
    }

    merch: inventoryItems(
      first: $first
      after: $merchCursor
      cityId: $cityId
      clientIds: $clientIds
      excludedTypeIds: [3] # Exclude flyers
      searchText: $searchText
      searchFromStart: $searchFromStart
      ambassadorIds: $ambassadorIds
      current: $current
      # When searching for merch we don't want to exclude by campaign ids
      # campaignIds: $campaignIds
      active: $active
      includeLeftovers: $includeLeftovers
    ){
      pageInfo {
        endCursor,
        hasNextPage
      },
      totalCount,
      edges {
        node {
          id,
          client { id },
          inventoryType { id },
          description,
          summarisedMovements {
            nodes {
              quantity,
              movementType { id },
              relatedUser {
                id,
                firstName,
                lastName,
              },
              relatedCity { id }
            }
          }
        }
      }
    }

    flyerCodes: inventoryItems(
      first: $first
      after: $flyerCodesCursor
      cityId: $cityId
      clientIds: $clientIds
      typeIds: [3] # Include only flyer codes
      searchText: $searchText
      searchFromStart: $searchFromStart
      ambassadorIds: $ambassadorIds
      current: $current
      # Filter by campaign for flyer codes
      campaignIds: $campaignIds
      active: $active
      includeLeftovers: $includeLeftovers
    ){
      pageInfo {
        endCursor,
        hasNextPage
      },
      totalCount,
      edges {
        node {
          id,
          client { id },
          inventoryType { id },
          description,
          tag
          summarisedMovements {
            nodes {
              quantity,
              movementType { id },
              relatedUser {
                id,
                firstName,
                lastName,
              },
              relatedCity { id }
            }
          }
        }
      }
    }
  }
`

/**
 * Get pickup data
 * @param {string} authToken
 * @param {object} params
 */
function getPickupInventoryData(
  authToken,
  first,
  { merchCursor, flyerCodesCursor },
  {
    cityId,
    searchText,
    clientIds,
    typeId,
    ambassadorIds,
    current,
    campaignIds,
    active,
    includeLeftovers,
  },
) {
  return callGraphQLEndpoint(authToken, getPickupInventoryGraphQLFunc, {
    first,
    merchCursor,
    flyerCodesCursor,
    cityId,
    clientIds,
    typeId,
    searchText,
    current,
    ambassadorIds,
    campaignIds,
    active,
    includeLeftovers,
  })
}

/**
 * Add an inventory item
 * @param {InventoryItemInput} item Required props are clientId, invento
 */
const addInventoryItem = function(authToken, item) {
  return callGraphQLEndpoint(
    authToken,
    `mutation addInventoryItem(
      $input: AddInventoryItemInput
    ) {
      addInventoryItem(
        input: $input,
      ){
        id,
        client { id },
        inventoryType { id },
        description,
        tag,
        movements {
          nodes {
            id,
            quantity,
            movementType { id },
            relatedUser { id },
            relatedCity { id },
            comments,
            dateTime

          }
        }
      }
    }`,
    {
      input: item,
    },
  )
}

/**
 * Add flyer codes in bulk
 * @param {Array<Object>} items
 */
const addFlyerCodes = async (authToken, flyerCodes) => {
  const mutation = `mutation addFlyerCodes(
    $input: [AddFlyerCodeInput]
  ) {
    addFlyerCodes(
      input: $input,
    ){
      flyerCodes {
        id,
        client { id },
        inventoryType { id },
        description,
        tag,
        movements {
          nodes {
            id,
            quantity,
            movementType { id },
            relatedUser { id },
            relatedCity { id },
            comments,
            dateTime
          }
        }
      }
    }
  }`

  const chunks = splitArrayIntoChunks({
    inputArray: flyerCodes,
    itemsPerChunk: 150,
  })

  const results = await Promise.all(
    chunks.map(async items => {
      const variables = {
        input: items,
      }
      const req = await callAuthorizedGraphQLEndpoint(mutation, variables)
      return req.json()
    }),
  )

  const createdFlyerCodes = results.reduce((acc, result) => {
    acc = [...acc, ...result.data.addFlyerCodes.flyerCodes]
    return acc
  }, [])

  return createdFlyerCodes
}

const deleteFlyerCodeById = async (authToken, id) =>
  callGraphQLEndpoint(
    authToken,
    `mutation deleteFlyerCode(
      $input: DeleteFlyerCodeInput
    ) {
      deleteFlyerCode(
        input: $input,
      )
    }`,
    {
      input: { id },
    },
  )

/**
 * Get inventory item
 * @param {string} authToken
 * @param {number} id
 */
function getInventoryItem(authToken, id) {
  return callGraphQLEndpoint(authToken, getInventoryItemGraphQLFunc, {
    id,
  })
}

/**
 * Gets the mission results for a campaign.
 * @param {string} authToken
 * @param {number} campaignId
 */
function getCampaignResults(authToken, campaignId) {
  var query = `query ($campaignId:ID!) {
    campaign(id:$campaignId){
      id
      name
      pitchUrl
      type
      bonusLimit
      status
      metrics {
        nodes {
          id
          name
          targetConversionRate
          targetCostPerMetric
          isPrimary
        }
      }
      client {
        id
        logo
      }
      cityPlans {
        nodes {
          id
          payout
          distributionCost
          teamLeaderPayAdjustment
          missions {
            edges {
              node {
                id
                flyerCode
                status
                totalFlyersDistributed
                dateTime
                distributionCost
                draftPayAdjustment
                printingCostPerFlyer
                packSize
                bonusPerConversion
                bonusRedemptionPeriod
                cancelled
                automatic
                type
                label
                user {
                  id
                  firstName
                  lastName
                }
                location {
                  id
                  name
                  hasPermit
                  rank
                  detailedRank
                  type {
                    name
                  }
                }
                metricResults {
                  nodes {
                    id
                    value
                    metric {
                      id
                    }
                  }
                }
                conversions {
                  nodes {
                    id
                    timestamp
                    metric {
                      id
                    }
                  }
                }
                boundary {
                  latitude
                  longitude
                }
                boundaryColour
              }
            }
          }
          city {
            id
            name
            lunchCheckInTime
            afternoonCheckInTime
            teamLeaderEnabled
            latitude
            longitude
            country {
              id
              name
              code
            }
            locations{
              edges {
                node {
                  id
                  name
                  hasPermit
                  rank
                  detailedRank
                  type {
                    name
                  }
                }
              }
            }
            brandAmbassadors {
              edges {
                node {
                  id
                  firstName
                  lastName
                }
              }
            }
          }
          permitCosts {
            nodes {
              id
              cost
              startDate
            }
          }
        }
      }
    }
  }`

  return callGraphQLEndpoint(authToken, query, {
    campaignId,
  })
}

/**
 * @typedef GetMissionsByCityQueryVars
 * @property {number} cityId Mandatory city id
 * @property {number} clientId Filter by client
 * @property {number} locationId Filter by location
 * @property {string} statusAndAbove Filter by status
 * @property {string} statusAndBelow Filter by status
 * @property {string} timeOfDay Filter by time of day
 * @property {number} campaignId Filter by campaign
 * @property {string} startDate Filter by start date
 * @property {string} endDate Filter by end date
 * @property {string} dayOfTheWeek Filter by day of the week
 *
 * Get missions within a city
 * @param {GetMissionsByCityQueryVars} queryVars
 */
const getMissionsByCity = function(authToken, queryVars) {
  var query = `query (
    $first:Int
    $skip:Int
    $cityId:ID!
    $clientId:ID
    $locationId:ID
    $campaignId:ID
    $startDate:String
    $endDate:String
    $statusAndAbove: MissionStatusEnum
    $statusAndBelow: MissionStatusEnum
    $timeOfDay: MissionTimeOfDayEnum
    $filter:String
    $orderBy:MissionOrderByEnum
    $dayOfTheWeek:DayOfTheWeekEnum
    $reported: Boolean
  ) {
    city(id: $cityId){
      id
      name
      country { id }
      lunchCheckInTime
      afternoonCheckInTime
      missions(
        first: $first
        skip: $skip
        clientId: $clientId
        locationId: $locationId
        campaignId: $campaignId
        startDate: $startDate
        endDate: $endDate
        statusAndAbove: $statusAndAbove
        statusAndBelow: $statusAndBelow
        timeOfDay: $timeOfDay
        filter: $filter
        orderBy: $orderBy
        dayOfTheWeek: $dayOfTheWeek
        reported: $reported
      ){
        edges {
          node {
            id
            dateTime
            flyerCode
            flyerCodeV2 {
              tag
              code
            }
            status
            totalFlyersDistributed
            rating
            cityPlan {
              campaign {
                client {
                  name
                }
                metrics {
                  nodes {
                    id
                    name
                  }
                }
              }
              city {
                afternoonCheckInTime
                lunchCheckInTime
              }
            }
            location {
              name
            }
            user {
              firstName
              lastName
            }
            metricResults {
              nodes {
                value
                metric {
                  id
                  name
                }
              }
            }
            amountScans
            canBeSpied
            spyInformation{
              spy{
                id
                firstName
                lastName
              }
              spyDetails{
                checkInTime
                checkoutTime
                reportReason
                }
            }
          }

        }
        pageInfo {
          hasNextPage
        }
        totalCount
      }
    }
  }`

  return callGraphQLEndpoint(authToken, query, queryVars)
}

const getMasterScheduleDetails = function(authToken, queryVars) {
  const query = `query ($campaignId:ID!) {
    campaign(id:$campaignId){
      cityPlans {
        nodes {
          city {
            brandAmbassadors {
              totalCount
              edges {
                node {
                  id
                  firstName
                  lastName
                }
              }
            }
            locations {
              edges {
                node {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
  }`
  return callGraphQLEndpoint(authToken, query, queryVars)
}

/**
 * @typedef UpdateUserData
 * @property {number} status User status. enum[active|blocked]
 * @property {number} blockedReason Reason of user block operation. Required for status == 'blocked'
 *
 * Update user information, such as status and block reason
 * @param {number} userId User ID
 * @param {UpdateUserData} userData
 */
const updateUserData = function(authToken, userId, userData) {
  return callGraphQLEndpoint(
    authToken,
    `mutation updateUserData ($input: UpdateUserInput) {
      updateUserData(input: $input){
        user {
          id
          firstName
          lastName
          blockedReason
          status
          isTeamLeader
        }
      }
    }`,
    {
      input: { id: userId, ...userData },
    },
  )
}

/**
 * @typedef UpdateCity
 * @property {boolean} recruitmentOnHold Whether this city recruits new BAs or not
 *
 * Update city information, such as `recruitmentOnHold`
 * @param {number} cityId City ID
 * @param {UpdateCity} cityData
 */
const updateCity = function(authToken, cityId, cityData) {
  return callGraphQLEndpoint(
    authToken,
    `mutation updateCitySettings ($input: UpdateCityInput) {
      updateCitySettings(input: $input){
        city {
          id
          recruitmentOnHold
          name
          timezone
          statusV2
          newcomersMissionsLimit
          cbdRadius
          surgeInterval
          pickupInformation
          morningCheckingTime
          morningCheckingTimeBefore
          morningCheckingTimeAfter
          lunchCheckingTime
          lunchCheckingTimeBefore
          lunchCheckingTimeAfter
          afternoonCheckingTime
          afternoonCheckingTimeBefore
          afternoonCheckingTimeAfter
          checkinRadius
          payHourly
          recruitmentCalendarId
          onboardingLocation
          onboardingUrl
          teamLeaderEnabled
          location {
            latitude
            longitude
          }
        }
      }
    }`,
    {
      input: { cityId, ...cityData },
    },
  )
}

/**
 * Create a city
 */
const createCity = function(authToken, cityData) {
  return callGraphQLEndpoint(
    authToken,
    `mutation createCity ($input: CreateCityInput) {
      createCity(input: $input){
        city {
          id
          recruitmentOnHold
          name
          timezone
          statusV2
          newcomersMissionsLimit
          cbdRadius
          surgeInterval
          pickupInformation
          morningCheckingTime
          morningCheckingTimeBefore
          morningCheckingTimeAfter
          lunchCheckingTime
          lunchCheckingTimeBefore
          lunchCheckingTimeAfter
          afternoonCheckingTime
          afternoonCheckingTimeBefore
          afternoonCheckingTimeAfter
          checkinRadius
          payHourly
          recruitmentCalendarId
          onboardingLocation
          onboardingUrl
          location {
            latitude
            longitude
          }
        }
      }
    }`,
    {
      input: cityData,
    },
  )
}

/**
 * Create a city spy settings
 */
const creatCitySpySettings = function(spySettingsData) {
  return callAuthorizedGraphQLEndpoint(
    `mutation ($input: CreateSpyCitySettingsInput!) {
      createSpyCitySettings(input:$input){
          id
          cityId
          hourlyRate
          maxBudgetPerWeek
          travelAllowancePerMission
          travelAllowanceCap
          durationHandToHandMission
          durationLetterboxMission
          durationShift
          missionsPerShift
      }
    }`,
    {
      input: spySettingsData,
    },
  )
}

/**
 * Update a city spy settings
 */
const updateCitySpySettings = function(spySettingsData) {
  return callAuthorizedGraphQLEndpoint(
    `mutation ($input: UpdateSpyCitySettingsInput!) {
      updateSpyCitySettings(input:$input){
          id
          cityId
          hourlyRate
          maxBudgetPerWeek
          travelAllowancePerMission
          travelAllowanceCap
          durationHandToHandMission
          durationLetterboxMission
          durationShift
          missionsPerShift
      }
    }`,
    {
      input: spySettingsData,
    },
  )
}

export const chatParticipantsSearch = (authToken, searchedString) => {
  const query = `
  query ($searchedString: String!) {
    chatParticipantsSearch(searchedString: $searchedString){
      cities {
        id
        name
        type
      }
      users {
        id
        name
        type
      }
    }
  }`
  return callGraphQLEndpoint(authToken, query, {
    searchedString,
  })
}
const deleteUser = function(authToken, userId) {
  return callGraphQLEndpoint(
    authToken,
    `mutation deleteUser ($input: DeleteUserInput) {
      deleteUser(input: $input){
        user {
          status
        }
      }
    }`,
    {
      input: { id: userId },
    },
  )
}

/**
 * Gets ambassador profile by id
 */
const getAmbassador = async (authToken, { id }) =>
  callGraphQLEndpoint(
    authToken,
    `query ($id:ID!) {
      brandAmbassador(ambassadorId:$id){
        id
        firstName
        lastName
        status
        blockedReason
        referralCode
        businessNumber
        insuranceNumber
        documentsProvided
        email
        extendedMissionsLimit
        fixedPriceMissions
        flyerCount
        isVerified
        itemCount
        memberSince
        mobilePhone
        profilePicture
        rating
        withholdPay
        isTeamLeader
        restrictedMissionType
        paymentDetails {
          accountName
          accountNumber
          branchNumber
        }
        city {
          id
          name
          teamLeaderEnabled
        }
        formW9 {
          fileName
          fileSaveTime
        }
        missions{
          edges {
            node {
              id
              status
              dateTime
              cityPlan {
                city {
                  name
                }
                campaign {
                  client {
                    name
                  }
                }
              }
              rating
              location {
                name
              }
            }
          }
          totalCount
        }
        signedLegalDocuments {
          totalCount
          edges {
            node {
              dateTime
              legalDocument {
                id
                url
                name
              }
            }
          }
        }
      }
    }`,
    { id },
  )

/**
 * Gets list of ambassador profile by city id
 */
const getAmbassadorsList = async (authToken, { cityId }) =>
  callGraphQLEndpoint(
    authToken,
    `query ($cityId:ID!) {
    brandAmbassadors(cityId:$cityId){
      totalCount
      edges{
        node {
          id
          firstName
          lastName
          status
          blockedReason
          referralCode
          businessNumber
          insuranceNumber
          documentsProvided
          email
          extendedMissionsLimit
          fixedPriceMissions
          flyerCount
          isVerified
          itemCount
          memberSince
          mobilePhone
          profilePicture
          rating
          withholdPay
          isTeamLeader
          paymentDetails {
            accountName
            accountNumber
            branchNumber
          }
          city {
            id
            name
          }
          missions{
            totalCount
            edges{
              node{
                status
              }
            }
          }
          futureMissions: missions(statusAndAbove:BOOKED statusAndBelow:CHECKED_IN) {
            totalCount
          }
        }
      }
    }
  }`,
    { cityId },
  )

/**
 * Gets all the commonly used data on the city dashboards
 */
const getCommonCityData = function(authToken, queryVars) {
  const query = `query ($cityId:ID!) {

    city(id: $cityId){
      id
      name
      location{
        latitude
        longitude
      }
      lunchCheckInTime
      afternoonCheckInTime
      payHourly
      recruitmentOnHold
      recruitmentCalendarId
      onboardingLocation
      onboardingUrl
      timezone
      statusV2
      cbdRadius
      pickupInformationURL
      morningCheckInTimeBefore
      morningCheckInTimeAfter
      lunchCheckInTimeBefore
      lunchCheckInTimeAfter
      afternoonCheckInTimeBefore
      afternoonCheckInTimeAfter
      checkinRadius
      newcomersMissionsLimitV2
      surgeInterval
      teamLeaderEnabled
      location {
        latitude
        longitude
      }
      country {
        code
      }
      clients {
        edges {
          node{
            id
            name
            logo
          }
        }
        totalCount
      }
      locations {
        edges {
          node {
            id
            name
            label
            moments{
              morning
              lunch
              afternoon
            }
            isSheltered
            hasPermit
            isEvent
            status
          }
        }
      }
      campaigns {
        edges {
          node {
            id
            name
            status
            client { id }
          }
        }
      }
      spies(statuses:[ACTIVE]){
          edges{
              node{
                id
                firstName
                lastName
                status
              }
          }
      }
      spySettings{
        id
        cityId
        hourlyRate
        maxBudgetPerWeek
        travelAllowancePerMission
        travelAllowanceCap
        durationHandToHandMission
        durationLetterboxMission
        durationShift
        missionsPerShift
      }
    }
  }`
  return callGraphQLEndpoint(authToken, query, queryVars)
}

/**
 * Gets all the commonly used data on the city dashboards
 */
const getCityBrandAmbassadors = function(queryVars) {
  const query = `query (
    $cityId:ID!, 
    $includeInactiveWithInventory: Boolean, 
    $includeInactiveWithMissions: Boolean,
    $hasInventory: Boolean, 
    $hasMissionsBooked: Boolean,
    $statuses: [UserStatusEnum!]
    ) {
    city(id: $cityId){
      brandAmbassadors(
        hasInventory: $hasInventory,
        hasMissionsBooked: $hasMissionsBooked,
        statuses: $statuses,
        includeInactiveWithInventory: $includeInactiveWithInventory,
        includeInactiveWithMissions: $includeInactiveWithMissions
        ) {
        edges {
          node {
            id
            firstName
            lastName
            status
          }
        }
      }
    }
  }`
  return callAuthorizedGraphQLEndpoint(query, queryVars)
}

/**
 * Gets all the countries
 */
const getCountries = function(authToken) {
  const query = `query {
    countries {
      nodes {
        id
        name
      }
    }
  }`
  return callGraphQLEndpoint(authToken, query)
}

/**
 * Get the upcoming pickup missions
 * @param {number} cityId To filter inventory by this city
 * @returns {Array<InventoryItem>}
 */
const getPickupMissionGraphQLFunc = `
  query getPickupMissionData(
    $cityId: ID!
    $now: String!
  ) {
    city(id:$cityId){
      lunchCheckInTime
      afternoonCheckInTime
      upcomingMissions: missions(statusAndAbove:BOOKED, statusAndBelow:BOOKED,startDate:$now){
        totalCount
        edges {
          node {
						id
            cityPlan {
							campaign {
                id
                name
                client {
                  id
                }
              }
            }
            dateTime
            user {
              id
              firstName
              lastName
              mobilePhone
              email
            }
            pickupDateTime
          }
        }
      }
      checkedInMissions: missions(statusAndAbove:CHECKED_IN, statusAndBelow:CHECKED_IN){
        totalCount
        edges {
          node {
						id
            cityPlan {
							campaign {
                name
                client {
                  id
                }
              }
            }
            dateTime
            user {
              id
              firstName
              lastName
              mobilePhone
              email
            }
            pickupDateTime
          }
        }
      }
    }
  }
`

/**
 * Get pickup missions data
 * @param {string} authToken
 * @param {object} params
 */
function getPickupMissionData(authToken, { cityId, now }) {
  return callGraphQLEndpoint(authToken, getPickupMissionGraphQLFunc, {
    cityId,
    now,
  })
}

const getPitchQuestions = ({ authToken, campaignId }) => {
  const query = `
    query ($campaignId: ID!){
      pitchQuestions (campaignId:$campaignId){
        id
        description
        type
        correctAnswer
        choices
      }
    }
  `
  const variables = { campaignId }
  return callGraphQLEndpoint(authToken, query, variables)
}

const setPitchQuestions = ({ authToken, campaignId, pitchQuestionsData }) => {
  const mutation = `
    mutation setPitchQuestions ($input: SetPitchQuestionsInput) {
      setPitchQuestions(input: $input){
        pitchQuestions {
          description
          type
          correctAnswer
          choices
        }
      }
    }
  `
  const variables = {
    input: {
      campaignId: Number(campaignId),
      pitchQuestions: pitchQuestionsData,
    },
  }
  return callGraphQLEndpoint(authToken, mutation, variables)
}

const unbookMission = (authToken, { missionId, unbookReason }) =>
  callGraphQLEndpoint(
    authToken,
    `
    mutation($input: UnbookMissionInput){
      unbookMission(input: $input){
        mission{
          id
          status
          user {
            id
          }
        }
      }
    }
  `,
    { input: { id: missionId, unbookReason } },
  )

const updateCityPlanGQL = (id, payload) => {
  const mutation = `
    mutation updateCityPlanSettings ($input: UpdateCityPlanInput) {
      updateCityPlanSettings(input: $input) {
        cityPlan {
          teamLeaderPayAdjustment
          hourlyPayout
          clientMarginPercent
          distributionCostV2
          payout
        }
      }
    }
  `
  const {
    distributionCost,
    payout,
    teamLeaderPayAdjustment,
    hourlyPayout,
    clientMarginPercent,
  } = payload

  const variables = {
    input: {
      id,
      distributionCost: distributionCost,
      payout: payout,
      hourlyPayout: hourlyPayout,
      clientMarginPercent: clientMarginPercent,
      teamLeaderPayAdjustment: teamLeaderPayAdjustment,
    },
  }

  return callAuthorizedGraphQLEndpoint(mutation, variables)
}

/**
 * Notify missions successfully spied
 * @param {number[]} missionIds
 */
const notifySuccessfullySpiedMissions = missionIds =>
  callAuthorizedGraphQLEndpoint(
    `mutation notifySuccessfullySpiedMissions(
      $input: NotifySpiedMissionsInput
    ) {
      notifySuccessfullySpiedMissions(
        input: $input,
      ) { success }
    }`,
    {
      input: { missionIds },
    },
  )

/**
 * Gets all the data needed to edit and view a mission.
 */
const getMissionViewDataById = id =>
  callAuthorizedGraphQLEndpoint(
    `
    query($id: ID!){
      mission(id: $id){
        id
        status
        type
        user { 
          id
          firstName
          lastName
          profilePicture
          isTeamLeader
        }
        client { id name }
        campaign { id name }
        location { 
          id
          name
          longitude
          latitude
        }
        cityPlan {
          id
          city { 
            id
            name
            timezone
            lunchCheckInTime
            afternoonCheckInTime
            morningCheckingTime
            morningCheckingTimeBefore
            morningCheckingTimeAfter
            lunchCheckingTime
            lunchCheckingTimeBefore
            lunchCheckingTimeAfter
            afternoonCheckingTime
            afternoonCheckingTimeBefore
            afternoonCheckingTimeAfter
            country {
              code
              currencySign
            }
            locations{
              edges {
                node {
                  id
                  name
                  longitude
                  latitude
                }
              }
            }
            brandAmbassadors {
              edges {
                node {
                  id
                  statusV2
                  firstName
                  lastName
                }
              }
            }
          }
        }
        totalFlyersDistributed
        flyerCodeV2 {
          id
          code
          quantity
        }
        checkInTime
        checkInSelfie
        checkInFlyerCodeImage
        checkInFlyerPackImage
        checkInLocation {
          latitude
          longitude
        }
        checkoutTime
        checkoutStepsWalked
        checkoutLocation {
          latitude
          longitude
        }
        totalFlyersDistributed
        payoutRatio
        payoutRatioComment
        pickupDateTime
        draftPayAdjustment
        comments
        timeRangeStart
        recommendedCheckInTime { from }
        notifiedSuccessfullySpied
        dateTime
        distributionCostV2
        printingCostPerFlyer
        draftPayAdjustment
        printingCost
        conversions {
          nodes {
            id
            timestamp
            metric { id name }
          }
        }
        boundary {
          latitude
          longitude
        }
        boundaryColour
        flyerCodes {
          edges {
            node {
              id
              quantity
              code
            }
          }
        }
        activities {
          edges {
            node {
              dateTime
              position {
                latitude
                longitude
              }
            }
          }
        }
        costPerFlyer
        amountScans
        canBeSpied
        spyInformation {
          spy {
            id
            firstName
            lastName
            status
          }
          spyDetails {
            checkInTime
            checkoutTime
            reportReason
          }
        }
      }
    }
  `,
    { id },
  )

/**
 * Create missions
 * @param {Array<Object>} args.mission
 */
const createMissions = async missions => {
  const mutation = `
  mutation createMissions ($input: CreateMissionsInput!) {
    createMissions(input: $input) {
      missions { 
        id
        status
        dateTime
        distributionCost
        costPerFlyer
        draftPayAdjustment
        printingCostPerFlyer
        bonusPerConversion
        bonusRedemptionPeriod
        type
        label
        location {
          id
          name
          hasPermit
          rank
          detailedRank
          type {
            name
          }
        }
        cityPlan { 
          id 
        }
        boundary {
          latitude
          longitude
        }
        boundaryColour
        amountScans
      }
    }
  }
`

  const chunks = splitArrayIntoChunks({
    inputArray: missions,
    itemsPerChunk: 150,
  })

  const results = await Promise.all(
    chunks.map(async missions => {
      const variables = {
        input: { missions },
      }
      const req = await callAuthorizedGraphQLEndpoint(mutation, variables)
      return req.json()
    }),
  )

  const createdMissions = results.reduce((acc, result) => {
    acc = [...acc, ...result.data.createMissions.missions]
    return acc
  }, [])

  return createdMissions
}

const updateMission = ({ missionInput }, options) => {
  const mutation = `
  mutation updateMission ($input: UpdateMissionInput!) {
    updateMission(input: $input) {
      mission { 
        id
        status
        type
        label
        user { 
          id
          firstName
          lastName
          profilePicture
          isTeamLeader
        }
        client { id name }
        campaign { id name }
        location { 
          id
          name
          longitude
          latitude
        }
        cityPlan {
          id
          city { 
            id
            name
            timezone
            lunchCheckInTime
            afternoonCheckInTime
            locations{
              edges {
                node {
                  id
                  name
                  longitude
                  latitude
                }
              }
            }
            brandAmbassadors {
              edges {
                node {
                  id
                  firstName
                  lastName
                }
              }
            }
          }
        }
        totalFlyersDistributed
        flyerCodeV2 {
          id
          code
          quantity
        }
        checkInTime
        checkInSelfie
        checkInFlyerCodeImage
        checkInFlyerPackImage
        checkInLocation {
          latitude
          longitude
        }
        checkoutTime
        checkoutStepsWalked
        checkoutLocation {
          latitude
          longitude
        }
        totalFlyersDistributed
        payoutRatio
        payoutRatioComment
        pickupDateTime
        draftPayAdjustment
        comments
        timeRangeStart
        recommendedCheckInTime { from }
        notifiedSuccessfullySpied
        dateTime
        distributionCostV2
        printingCostPerFlyer
        draftPayAdjustment
        printingCost
        conversions {
          nodes {
            id
            timestamp
            metric { id name }
          }
        }
        boundary {
          latitude
          longitude
        }
        boundaryColour
        flyerCodes {
          edges {
            node {
              id
              quantity
              code
            }
          }
        }
        canBeSpied
        spyInformation {
          spyDetails {
            checkInTime
          }
        }
      }
    }
  }
`

  const variables = {
    input: missionInput,
  }

  return callAuthorizedGraphQLEndpoint(mutation, variables, options)
}
export const createCampaign = payload =>
  callAuthorizedGraphQLEndpoint(
    `mutation createCampaign(
    $input: CreateCampaignInput
  ) {
    createCampaign(
      input: $input,
    ) { 
      campaign { 
        id 
        type
      }
    }
  }`,
    {
      input: {
        ...payload,
        // legacy prop that wants the input in dollars
        printingCostPerFlyer: payload.printingCostPerFlyer
          ? payload.printingCostPerFlyer / 100
          : payload.printingCostPerFlyer,
      },
    },
  )

const updateCampaign = payload => {
  return callAuthorizedGraphQLEndpoint(
    `mutation updateCampaign(
    $input: UpdateCampaignInput
  ) {
    updateCampaign(
      input: $input,
    ) { 
      campaign { id }
    }
  }`,
    {
      input: {
        ...payload,
        // legacy prop that wants the input in dollars
        printingCostPerFlyer: payload.printingCostPerFlyer
          ? payload.printingCostPerFlyer / 100
          : payload.printingCostPerFlyer,
      },
    },
  )
}

const getCampaignById = (campaignId, { includeMissions } = {}) =>
  callAuthorizedGraphQLEndpoint(
    `query ($campaignId:ID!) {
      campaign(id:$campaignId){
        id
        name
        pitchUrl
        type
        status
        printingCostPerFlyer: printingCostPerFlyerV2 #Get it in cents
        conversionsCount
        bonusEnabled
        bonusLimit
        payoutPerConversion
        baRedemptionPeriod
        distributionCost
        basePayout
        metrics {
          nodes {
            id
            name
            targetConversionRate
            targetCostPerMetric
            isPrimary
          }
        }
        client {
          id
          logo
        }
        country {
          id
        }
        ${
          includeMissions
            ? `
        cityPlans {
          nodes {
            id
            city { 
              id
            }
            missions {
              edges {
                node {
                  id,
                  boundaryColour,
                  dateTime,
                  status,
                  label,
                  boundary {
                    latitude
                    longitude
                  }
                }
              }
            }
          }
        }`
            : ''
        }
      }
    }`,
    { campaignId },
  )

/**
 * Gets all the Campaigns
 */
const getCampaigns = ({ statuses } = { statuses: 'ACTIVE' }) => {
  const query = `
    query ($statuses:[CampaignStatusEnum!]) {
      campaigns (statuses: $statuses) {
        edges {
          node {
            id
            name
            pitchUrl
            type
            status
            countryId
            metrics {
              nodes {
                id
                name
                targetConversionRate
                targetCostPerMetric
                isPrimary
              }
            }
            cityPlans {
              nodes{
                id
                city {
                  id
                  name
                  statusV2
                }
              }
            }
            client {
              id
              name
              logo
            }
          }
        }
      }
    }`
  return callAuthorizedGraphQLEndpoint(query, { statuses })
}

/**
 * Gets all the Cities
 */
const getCities = () => {
  const query = `
    query {
      cities {
        edges {
          node {
            id
            name
            lunchCheckInTime
            afternoonCheckInTime
            payHourly
            recruitmentOnHold
            recruitmentCalendarId
            onboardingLocation
            onboardingUrl
            timezone
            statusV2
            cbdRadius
            pickupInformationURL
            morningCheckInTimeBefore
            morningCheckInTimeAfter
            lunchCheckInTimeBefore
            lunchCheckInTimeAfter
            afternoonCheckInTimeBefore
            afternoonCheckInTimeAfter
            checkinRadius
            newcomersMissionsLimitV2
            surgeInterval
            teamLeaderEnabled
            location {
              latitude
              longitude
            }
            country {
              id
              name
              code
            }
          }
        }
      }
    }`
  return callAuthorizedGraphQLEndpoint(query)
}

const createCityPlan = cityPlan => {
  const mutation = `mutation createCityPlan($input: CreateCityPlanInput){
      createCityPlan(input: $input){
            cityPlan {
              id
              hourlyPayout
              clientMarginPercent
              payout
              distributionCost
        }
      }
    }`

  const variables = {
    input: { ...cityPlan },
  }

  return callAuthorizedGraphQLEndpoint(mutation, variables)
}

const createCityPlans = cityPlans => {
  const mutation = `
    mutation createCityPlans ($input: CreateCityPlansInput!) {
      createCityPlans(input: $input) {
        cityPlans { 
          id
          city {
            id
          }
        }
      }
    }`

  const variables = {
    input: { cityPlans },
  }

  return callAuthorizedGraphQLEndpoint(mutation, variables)
}

const getCityPlans = options => {
  const query = `query($campaignId:ID, $cityId:ID) {
    cityPlans(campaignId: $campaignId, cityId: $cityId) {
      edges {
        node {
          id
          city {
            id
          }
        }
      }
    }
  }`

  return callAuthorizedGraphQLEndpoint(query, options)
}

const getClients = ({ statuses = ['ACTIVE'], first, skip } = {}) => {
  const query = `
  query($statuses:[ClientStatusEnum], $first: Int, $skip: Int) {
    clients(statuses: $statuses, first: $first, skip: $skip) {
      edges{
        cursor
        node {
          id
          name
          logo
          status
        }
      }
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }`

  return callAuthorizedGraphQLEndpoint(query, { statuses, first, skip })
}

/**
 * Grabs the mission activity records from the API project.
 * Mission activities are now stored in Trailblazer, hence this is a legacy
 * method, however it needs to be used to view historic data.
 */
const getMissionActivitiesLegacy = id => {
  const query = `
    query($id: ID!){
      mission(id: $id){
        activities {
          edges {
            node {
              dateTime
              position {
                latitude
                longitude
              }
            }
          }
        }
      }
    }`
  return callAuthorizedGraphQLEndpoint(query, { id })
}

/**
 * Grabs the mission activity records.
 */
const getMissionActivities = ({
  users,
  from,
  to,
  after,
  missions,
  onlyLastPoint,
}) => {
  const query = `
    query($dateRange: DateRangeSearch, $after: String, $users: [ID!], $missions: [ID!], $onlyLastPoint: Boolean) {
      searchFeatures(dateRange: $dateRange, after: $after, users: $users, missions: $missions, onlyLastPoint: $onlyLastPoint){
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        collection {
          type
          features {
            type
            properties
            geometry {
              type
              coordinates
            }
          }
        }
      }  
    }
  `
  return callTrailblazer(query, {
    from,
    to,
    after,
    users,
    missions,
    onlyLastPoint,
  })
}

const fetchCityPlanMissionFormChunks = async ({
  gql,
  cityPlanId,
  chunkSize = 200,
  skip = 0,
  ...params
}) => {
  const res = await callAuthorizedGraphQLEndpoint(gql, {
    cityPlanId,
    first: chunkSize,
    skip,
    ...params,
  })

  const { data } = await res.json()

  let missionsResult = data.cityPlans.edges[0].node.missions.edges

  if (data.cityPlans.edges[0].node.missions.pageInfo.hasNextPage) {
    const { missions } = await fetchCityPlanMissionFormChunks({
      cityPlanId,
      chunkSize,
      skip: chunkSize + skip,
      gql,
      ...params,
    })

    missionsResult = [...missionsResult, ...missions]
  }
  return {
    missions: missionsResult,
  }
}

const getCampaignByChunks = async ({
  campaignId,
  cityPlanChunk,
  missionChunk,
  missionStatuses,
  queries,
}) => {
  const { gqlCampaign, gqlCityPlans, gqlMissions } = queries
  const campaignResponse = await callAuthorizedGraphQLEndpoint(gqlCampaign, {
    campaignId,
  })

  const campaign = await campaignResponse.json()

  const campaignResult = campaign.data.campaign
  const cityPlansIds = campaign.data.campaign.cityPlans.nodes
    ? campaign.data.campaign.cityPlans.nodes.map(({ id }) => id)
    : []

  const cityPlans = await iteratePromisesInChunks(
    cityPlansIds,
    cityPlanChunk,
    async cityPlanId => {
      const cityPlanResponse = await callAuthorizedGraphQLEndpoint(
        gqlCityPlans,
        {
          cityPlanId,
        },
      )

      const { missions } = await fetchCityPlanMissionFormChunks({
        cityPlanId,
        chunkSize: missionChunk,
        skip: 0,
        gql: gqlMissions,
        missionStatuses,
      })

      const resCityPlanResponse = await cityPlanResponse.json()

      const cityPlan = resCityPlanResponse.data.cityPlans.edges[0].node

      return {
        ...cityPlan,
        missions: {
          edges: missions,
        },
      }
    },
  )

  return { ...campaignResult, cityPlans: { nodes: cityPlans } }
}

export {
  processInventoryMovements,
  getInventory,
  addInventoryItem,
  getInventoryItem,
  addFlyerCodes,
  getCampaignResults,
  getMissionsByCity,
  getMasterScheduleDetails,
  updateUserData,
  updateCity,
  deleteUser,
  getAmbassador,
  getAmbassadorsList,
  getCommonCityData,
  getCityBrandAmbassadors,
  createCity,
  getCountries,
  getPickupInventoryData,
  getPickupMissionData,
  getPitchQuestions,
  setPitchQuestions,
  unbookMission,
  deleteFlyerCodeById,
  updateCityPlanGQL,
  notifySuccessfullySpiedMissions,
  getMissionViewDataById,
  createMissions,
  updateMission,
  updateCampaign,
  getCampaignById,
  getCampaigns,
  getCities,
  createCityPlan,
  createCityPlans,
  getCityPlans,
  getClients,
  getMissionActivitiesLegacy,
  getMissionActivities,
  creatCitySpySettings,
  updateCitySpySettings,
  fetchCityPlanMissionFormChunks,
  getCampaignByChunks,
}



// WEBPACK FOOTER //
// ./src/Module/graphQL/index.js